import { retargetFirstPerson } from "./viewmodel.js";
import { sourceSkyMaterial, sourceLitMaterial } from "./source-sky.js";
import * as THREE from "three";
import { RoomEnvironment } from "three/addons/environments/RoomEnvironment.js";
import {
  animated,
  AnimatedAsset,
  manifest,
  loadAsset,
  registerInstances,
} from "./assets.js";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";
import { ConvexGeometry } from "three/addons/geometries/ConvexGeometry.js";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import type {
  Brush,
  MapData,
  State,
  Vec3,
  Profile,
  Cosmetic,
} from "../shared/types.js";
import { planes } from "../shared/collision.js";
import { settings } from "./settings.js";
function brushGeometry(b: Brush) {
  if (!b.planes) {
    const g = new THREE.BoxGeometry(
      ...(b.max.map((v, i) => v - b.min[i]) as Vec3),
    );
    g.translate(...(b.min.map((v, i) => (v + b.max[i]) / 2) as Vec3));
    return g;
  }
  const ps = planes(b),
    points: THREE.Vector3[] = [];
  for (let a = 0; a < ps.length; a++)
    for (let b = a + 1; b < ps.length; b++)
      for (let c = b + 1; c < ps.length; c++) {
        const na = new THREE.Vector3(...ps[a].n),
          nb = new THREE.Vector3(...ps[b].n),
          nc = new THREE.Vector3(...ps[c].n),
          cross = nb.clone().cross(nc),
          det = na.dot(cross);
        if (Math.abs(det) < 1e-7) continue;
        const point = cross
          .multiplyScalar(ps[a].d)
          .add(nc.clone().cross(na).multiplyScalar(ps[b].d))
          .add(na.clone().cross(nb).multiplyScalar(ps[c].d))
          .divideScalar(det);
        if (ps.every((p) => point.dot(new THREE.Vector3(...p.n)) <= p.d + 0.01))
          points.push(point);
      }
  return new ConvexGeometry(points);
}
function syncPreviewKnife(actor: AnimatedAsset) {
  const knife = actor.root.userData.previewKnife as THREE.Group | undefined;
  const family = actor.root.userData.previewFamily;
  const rig = actor.root.children.find(
    (o) =>
      o.name.includes("animation") &&
      o.name.includes(family) &&
      !o.name.includes("empty"),
  );
  if (knife && rig)
    knife.traverse((target) => {
      if (target instanceof THREE.Bone) {
        let source: THREE.Object3D | undefined;
        rig.traverse((o) => {
          if (
            o.name === target.name ||
            new RegExp("^" + target.name + "_[0-9]+$").test(o.name)
          )
            source = o;
        });
        if (source) {
          target.position.copy(source.position);
          target.quaternion.copy(source.quaternion);
          target.scale.copy(source.scale);
        }
      }
    });
}
function visibleBounds(root: THREE.Object3D, glovesOnly = false) {
  root.updateMatrixWorld(true);
  const box = new THREE.Box3();
  root.traverseVisible((o) => {
    if (glovesOnly && o.name !== "preview_glove") return;
    if (o instanceof THREE.SkinnedMesh) {
      o.skeleton.update();
      if (glovesOnly) {
        const materials = Array.isArray(o.material) ? o.material : [o.material];
        const geometry = o.geometry,
          index = geometry.index;
        const groups = geometry.groups.length
          ? geometry.groups
          : [
              {
                start: 0,
                count: index?.count ?? geometry.getAttribute("position").count,
                materialIndex: 0,
              },
            ];
        const vertex = new THREE.Vector3();
        for (const group of groups) {
          if (/bare_arm/i.test(materials[group.materialIndex ?? 0]?.name ?? ""))
            continue;
          for (let i = group.start; i < group.start + group.count; i++)
            box.expandByPoint(
              o
                .getVertexPosition(index ? index.getX(i) : i, vertex)
                .applyMatrix4(o.matrixWorld),
            );
        }
        return;
      }
      o.computeBoundingBox();
      if (o.boundingBox)
        box.union(o.boundingBox.clone().applyMatrix4(o.matrixWorld));
    } else if (o instanceof THREE.Mesh) {
      o.geometry.computeBoundingBox();
      if (o.geometry.boundingBox)
        box.union(o.geometry.boundingBox.clone().applyMatrix4(o.matrixWorld));
    }
  });
  return box;
}
function label(text: string, color = "#d5e6ba", size = 24) {
  const canvas = document.createElement("canvas");
  canvas.width = 512;
  canvas.height = 96;
  const ctx = canvas.getContext("2d")!;
  ctx.font = "600 32px monospace";
  ctx.fillStyle = color;
  ctx.textAlign = "center";
  ctx.fillText(text, 256, 58);
  const sprite = new THREE.Sprite(
    new THREE.SpriteMaterial({
      map: new THREE.CanvasTexture(canvas),
      transparent: true,
      depthTest: true,
    }),
  );
  sprite.scale.set(size * 5.33, size, 1);
  return sprite;
}
export class Renderer {
  renderer: THREE.WebGLRenderer;
  scene = new THREE.Scene();
  camera = new THREE.PerspectiveCamera(100, 1, 0.5, 18000);
  private world = new THREE.Group();
  private debug = new THREE.Group();
  private remotes = new Map<string, THREE.Group>();
  private ghost?: THREE.Group;
  private viewScene = new THREE.Scene();
  private viewCamera = new THREE.PerspectiveCamera(70, 1, 0.1, 1000);
  private viewmodel = new THREE.Group();
  private models = new Map<string, Promise<THREE.Group>>();
  private platformMeshes: THREE.Mesh[] = [];
  private inspector = 0;
  private arms?: AnimatedAsset;
  private knifeModel?: THREE.Group;
  private knifeKind = "knife";
  private equipGeneration = 0;
  private previews = new Map<string, AnimatedAsset>();
  private previewScene = new THREE.Scene();
  private previewCamera = new THREE.PerspectiveCamera(35, 1, 0.01, 100);
  private previewKey = "";
  private previewAsset?: AnimatedAsset;
  previewRotation = 0;
  previewZoom = 1;
  private thumbnails = new Map<string, string>();
  private thumbnailBusy = false;
  private previewPivot = new THREE.Group();
  private previewClass = "";
  private previewBounds = new THREE.Vector3(1, 2, 1);
  private previewShadow: THREE.Mesh;
  private map?: MapData;
  constructor(canvas: HTMLCanvasElement) {
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: settings.antialias,
      powerPreference: "high-performance",
    });
    this.renderer.setPixelRatio(
      Math.min(devicePixelRatio, 2) * settings.renderScale,
    );
    this.renderer.info.autoReset = false;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.setClearColor("#182823");
    this.scene.background = new THREE.Color("#263d36");
    this.scene.fog = new THREE.FogExp2("#263d36", 0.00022);
    this.scene.add(new THREE.HemisphereLight("#f2f3d6", "#35404b", 0.8));
    const sun = new THREE.DirectionalLight("#fff1cd", 1.8);
    sun.position.set(400, -300, 1200);
    this.scene.add(sun);
    this.scene.add(this.world, this.debug);
    this.debug.visible = false;
    this.camera.up.set(0, 0, 1);
    const environment = new THREE.PMREMGenerator(this.renderer).fromScene(
      new RoomEnvironment(),
      0.04,
    ).texture;
    this.viewScene.environment = environment;
    this.previewScene.environment = environment;
    this.scene.environment = environment;
    this.scene.environmentIntensity = 0.35;
    this.viewScene.add(new THREE.HemisphereLight(0xdde6f0, 0x454b52, 0.7));
    const viewKey = new THREE.DirectionalLight(0xffffff, 1.5);
    viewKey.position.set(-40, 70, 20);
    this.viewScene.add(viewKey);
    this.viewScene.environmentIntensity = 0.5;
    this.viewScene.add(this.viewmodel);
    this.previewScene.environmentIntensity = 0.9;
    this.previewScene.add(new THREE.HemisphereLight(0xe6edf4, 0x52545a, 0.65));
    const key = new THREE.DirectionalLight(0xfff4e8, 2.0);
    key.position.set(-3, 4, 5);
    const fill = new THREE.DirectionalLight(0xc4def5, 1.1);
    fill.position.set(4, 2, 3);
    const rim = new THREE.DirectionalLight(0xdbeaf9, 1.6);
    rim.position.set(2, 3, -4);
    this.previewScene.add(key, fill, rim, this.previewPivot);
    const shadowCanvas = document.createElement("canvas");
    shadowCanvas.width = shadowCanvas.height = 128;
    const ctx = shadowCanvas.getContext("2d")!;
    const gradient = ctx.createRadialGradient(64, 64, 0, 64, 64, 64);
    gradient.addColorStop(0, "rgba(0,0,0,.42)");
    gradient.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 128, 128);
    this.previewShadow = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.MeshBasicMaterial({
        map: new THREE.CanvasTexture(shadowCanvas),
        transparent: true,
        depthWrite: false,
      }),
    );
    this.previewShadow.rotation.x = -Math.PI / 2;
    this.previewScene.add(this.previewShadow);
    this.viewCamera.position.set(0, 0, 0);
    this.viewCamera.lookAt(0, 0, 100);
    this.resize();
    window.addEventListener("resize", () => this.resize());
  }
  resize() {
    this.renderer.setPixelRatio(
      Math.min(devicePixelRatio, 2) * settings.renderScale,
    );
    this.renderer.setSize(innerWidth, innerHeight);
    this.camera.aspect = innerWidth / innerHeight;
    this.camera.fov = settings.fov;
    this.camera.updateProjectionMatrix();
    this.viewCamera.aspect = this.camera.aspect;
    this.viewCamera.fov = settings.viewmodelFov;
    this.viewCamera.updateProjectionMatrix();
  }
  private clear(group: THREE.Group) {
    group.traverse((o) => {
      if (
        (o instanceof THREE.Mesh || o instanceof THREE.LineSegments) &&
        !o.userData.cachedAsset
      ) {
        o.geometry.dispose();
        for (const m of Array.isArray(o.material) ? o.material : [o.material])
          m.dispose();
      }
    });
    group.clear();
  }
  async load(map: MapData, progress: (phase: string) => void = () => {}) {
    progress("GEOMETRY / MATERIALS");
    this.map = map;
    if (this.scene.background instanceof THREE.CubeTexture)
      this.scene.background.dispose();
    this.scene.background = map.skybox
      ? await new THREE.CubeTextureLoader().loadAsync(map.skybox)
      : new THREE.Color("#263d36");
    if (this.scene.background instanceof THREE.CubeTexture)
      this.scene.background.colorSpace = THREE.SRGBColorSpace;
    this.scene.backgroundRotation.set(Math.PI / 2, 0, 0);
    this.scene.fog = map.geometry
      ? null
      : new THREE.FogExp2("#263d36", 0.00022);
    this.clear(this.world);
    this.clear(this.debug);
    this.platformMeshes = [];
    const groups = new Map<string, THREE.BufferGeometry[]>();
    for (const b of map.geometry ? [] : map.brushes) {
      const color =
        b.material && map.materials?.[b.material]
          ? "material:" + b.material
          : (b.color ?? "#5b6f65");
      const list = groups.get(color) ?? [];
      list.push(brushGeometry(b));
      groups.set(color, list);
    }
    for (const [key, geometries] of groups) {
      const materialData = key.startsWith("material:")
        ? map.materials?.[key.slice(9)]
        : undefined;
      const color = materialData?.color ?? (materialData ? "#627266" : key);
      const material = new THREE.MeshStandardMaterial({
        color,
        roughness: 0.95,
      });
      const texturePath =
        settings.textureQuality === "low"
          ? (materialData?.textureLow ?? materialData?.texture)
          : materialData?.texture;
      if (texturePath) {
        material.map = new THREE.TextureLoader().load(texturePath);
        material.map.colorSpace = THREE.SRGBColorSpace;
        material.map.anisotropy = 4;
      }
      const merged = mergeGeometries(geometries);
      if (merged) this.world.add(new THREE.Mesh(merged, material));
      for (const g of geometries) g.dispose();
    }
    const water = new THREE.Mesh(
      new THREE.PlaneGeometry(30000, 18000),
      new THREE.MeshStandardMaterial({
        color: "#254c44",
        roughness: 0.25,
        metalness: 0.35,
      }),
    );
    water.position.z = map.killZ - 25;
    if (!map.geometry) this.world.add(water);
    for (const t of map.triggers) {
      const color =
        t.type === "start"
          ? "#b9d892"
          : t.type === "end"
            ? "#d9b478"
            : t.type === "checkpoint"
              ? "#83c3c0"
              : "#d37c69";
      const box = new THREE.BoxGeometry(
        ...(t.max.map((v, i) => v - t.min[i]) as Vec3),
      );
      const wire = new THREE.LineSegments(
        new THREE.EdgesGeometry(box),
        new THREE.LineBasicMaterial({ color }),
      );
      wire.position.set(...(t.min.map((v, i) => (v + t.max[i]) / 2) as Vec3));
      this.debug.add(wire);
      box.dispose();
      if (["start", "end", "checkpoint"].includes(t.type)) {
        const plate = new THREE.Mesh(
          new THREE.PlaneGeometry(t.max[0] - t.min[0], t.max[1] - t.min[1]),
          new THREE.MeshBasicMaterial({
            color,
            transparent: true,
            opacity: 0.18,
            side: THREE.DoubleSide,
          }),
        );
        plate.position.set(
          (t.min[0] + t.max[0]) / 2,
          (t.min[1] + t.max[1]) / 2,
          t.min[2] + 0.5,
        );
        this.world.add(plate);
        const sign = label(
          t.type === "start"
            ? "START / 01"
            : t.type === "end"
              ? "FINISH"
              : "CHECKPOINT / " + ((t.stage ?? 0) + 1),
          color,
          30,
        );
        sign.position.set(
          (t.min[0] + t.max[0]) / 2,
          (t.min[1] + t.max[1]) / 2,
          t.min[2] + 130,
        );
        this.world.add(sign);
      }
    }
    for (const b of map.brushes.length > 1500 ? [] : map.brushes) {
      const wire = new THREE.LineSegments(
        new THREE.EdgesGeometry(brushGeometry(b)),
        new THREE.LineBasicMaterial({
          color: "#adc98b",
          transparent: true,
          opacity: 0.3,
        }),
      );
      this.debug.add(wire);
    }
    for (const p of map.platforms ?? []) {
      const mesh = new THREE.Mesh(
        brushGeometry(p.brush),
        new THREE.MeshStandardMaterial({ color: p.brush.color ?? "#b6ac81" }),
      );
      this.world.add(mesh);
      this.platformMeshes.push(mesh);
    }
    if (map.geometry) {
      const model = (await loadAsset(map.geometry)).scene.clone(true);
      let lightmap: THREE.Texture | undefined;
      if (map.lightmap) {
        lightmap = await new THREE.TextureLoader().loadAsync(map.lightmap);
        lightmap.colorSpace = THREE.SRGBColorSpace;
        lightmap.flipY = false;
        lightmap.channel = 1;
      }
      model.traverse((o) => {
        o.userData.cachedAsset = true;
        if (o instanceof THREE.Mesh) {
          const convert = (mat: THREE.Material) => {
            const m = mat as THREE.MeshStandardMaterial;
            if (/tools[\/]toolsskybox/i.test(m.name))
              return sourceSkyMaterial(this.scene);
            if (!lightmap || !o.geometry.getAttribute("uv1")) return m;
            return sourceLitMaterial(m, lightmap, map.lightmapIntensity ?? 1);
          };
          o.material = Array.isArray(o.material)
            ? o.material.map(convert)
            : convert(o.material);
        }
      });
      model.scale.setScalar(map.geometryScale ?? 1);
      if (map.geometryUp === "y") model.rotation.x = Math.PI / 2;
      if (this.map === map) this.world.add(model);
    }
    progress("READY");
  }
  private asset(path: string) {
    if (!this.models.has(path))
      this.models.set(
        path,
        new GLTFLoader().loadAsync(path).then((g) => g.scene),
      );
    return this.models.get(path)!;
  }
  private equippedKey = "";
  private loadout: Record<string, string> = {
    agent: "ct-default",
    knife: "knife-default",
    gloves: "gloves-default",
  };
  private knifeLinks: { source: THREE.Object3D; target: THREE.Object3D }[] = [];
  private grip?: THREE.Object3D;
  private gripPosition = new THREE.Vector3();
  private gripRotation = new THREE.Quaternion();
  private parentRotation = new THREE.Quaternion();
  private weaponBasis = new THREE.Quaternion(0.5, 0.5, 0.5, 0.5);
  metrics() {
    return {
      drawCalls: this.renderer.info.render.calls,
      triangles: this.renderer.info.render.triangles,
    };
  }
  equip(user: Profile, content: Cosmetic[]) {
    const key = JSON.stringify(user.equipped);
    if (key === this.equippedKey) return;
    this.equippedKey = key;
    this.loadout = { ...user.equipped };
    this.previewKey = "";
    for (const actor of this.previews.values()) actor.dispose();
    this.previews.clear();
    this.knifeLinks = [];
    const generation = ++this.equipGeneration;
    this.arms?.dispose();
    this.arms = undefined;
    this.knifeModel = undefined;
    this.viewmodel.clear();
    const knife = content.find((c) => c.id === user.equipped.knife);
    if (!knife?.asset) return;
    void manifest
      .then(async (m) => {
        if (!m.assets.arms) return;
        const glove =
          m.assets[user.equipped.gloves] ?? m.assets["gloves-default"];
        const [arms, model, gloves, agent] = await Promise.all([
          animated(m.assets.arms.url, "arms"),
          animated(knife.asset!, "item"),
          glove?.rig && user.equipped.gloves !== "gloves-default"
            ? loadAsset(glove.url)
            : Promise.resolve(null),
          m.assets[user.equipped.agent]
            ? animated(m.assets[user.equipped.agent].url, "arms")
            : Promise.resolve(null),
        ]);
        if (agent) {
          const replacements: THREE.SkinnedMesh[] = [];
          agent.root.traverse((o) => {
            if (
              !(o instanceof THREE.SkinnedMesh) ||
              !o.name.includes("firstperson")
            )
              return;
            replacements.push(retargetFirstPerson(o, arms.root));
          });
          if (replacements.length) {
            arms.root.traverse((o) => {
              if (o instanceof THREE.Mesh && o.name.includes("firstperson"))
                o.visible = false;
            });
            arms.root.add(...replacements);
          }
          agent.dispose();
        }
        if (gloves && glove.rig) {
          const bones = glove.rig.names.map(
            (name) => arms.root.getObjectByName(name) as THREE.Bone,
          );
          if (bones.every(Boolean)) {
            const skeleton = new THREE.Skeleton(
              bones,
              glove.rig.inverses.map((v) => new THREE.Matrix4().fromArray(v)),
            );
            arms.root.traverse((o) => {
              if (
                o instanceof THREE.Mesh &&
                o.name.includes("firstperson_default_gloves_arms") &&
                (Array.isArray(o.material) ? o.material : [o.material]).every(
                  (m) => m.name.includes("glove"),
                )
              )
                o.visible = false;
            });
            gloves.scene.traverse((o) => {
              if (
                o instanceof THREE.Mesh &&
                o.name.includes("viewmodel") &&
                !(Array.isArray(o.material) ? o.material : [o.material]).every(
                  (m) => m.name.includes("bare_arm"),
                )
              ) {
                const mesh = new THREE.SkinnedMesh(o.geometry, o.material);
                mesh.name = "equipped_glove";
                mesh.bind(skeleton, new THREE.Matrix4());
                mesh.frustumCulled = false;
                arms.root.add(mesh);
              }
            });
          }
        }
        if (generation !== this.equipGeneration) {
          arms.dispose();
          model.dispose();
          return;
        }
        this.arms = arms;
        this.knifeModel = model.root;
        this.knifeKind = knife.animationFamily ?? "knife_default_ct";
        this.viewmodel.add(arms.root, model.root);
        this.viewmodel.scale.setScalar(1 / 0.0254);
        this.grip = arms.root.getObjectByName("wpn");
        const animation = arms.root.children.find(
          (o) =>
            o.name.includes("animation") &&
            o.name.includes(this.knifeKind) &&
            !o.name.includes("empty"),
        );
        model.root.traverse((target) => {
          if (!(target instanceof THREE.Bone)) return;
          let source: THREE.Object3D | undefined;
          animation?.traverse((o) => {
            if (
              o.name === target.name ||
              new RegExp("^" + target.name + "_[0-9]+$").test(o.name)
            )
              source = o;
          });
          if (source) this.knifeLinks.push({ source, target });
        });
        this.playKnife("draw");
      })
      .catch(console.error);
  }
  private playKnife(kind: string, restart = false) {
    if (!this.arms) return;
    const family = this.knifeKind.startsWith("knife_default")
      ? "_default_knife"
      : this.knifeKind;
    const prefix =
      kind === "inspect" ? "lookat" : kind === "slash" ? "light_miss1" : kind;
    const clip = this.arms.clips.find(
      (c) =>
        c.name.includes("/" + family + "/") &&
        c.name.split("/").pop()!.startsWith(prefix),
    );
    if (clip) this.arms.play(clip.name, kind !== "idle", restart);
  }
  inspectPreview() {
    const actor = this.previewAsset;
    const clip = actor?.clips.find(
      (c) =>
        c.name.split("/").pop()!.startsWith("lookat") &&
        (!actor?.root.userData.previewFamily ||
          c.name.includes("/" + actor.root.userData.previewFamily + "/")),
    );
    if (clip) actor!.play(clip.name, true);
  }
  async showThumbnails() {
    for (const el of document.querySelectorAll<HTMLImageElement>(
      "[data-thumbnail-id]",
    )) {
      const url = this.thumbnails.get(el.dataset.thumbnailId!);
      if (url) el.src = url;
    }
    if (this.thumbnailBusy) return;
    this.thumbnailBusy = true;
    try {
      const m = await manifest;
      for (const el of document.querySelectorAll<HTMLImageElement>(
        "[data-thumbnail-id]",
      )) {
        const id = el.dataset.thumbnailId!,
          info = m.assets[id];
        if (!info || this.thumbnails.has(id)) continue;
        const actor = await this.previewActor(info);
        if (info.category === "gloves")
          actor.root.traverse((o) => {
            if (o instanceof THREE.Mesh && o.name !== "preview_glove")
              o.visible = false;
          });
        const box = visibleBounds(actor.root),
          size = box.getSize(new THREE.Vector3());
        actor.root.position.sub(box.getCenter(new THREE.Vector3()));
        const scene = new THREE.Scene();
        scene.environment = this.previewScene.environment;
        scene.environmentIntensity = 0.8;
        this.previewScene.children
          .filter((o) => o instanceof THREE.Light)
          .forEach((o) => scene.add(o.clone()));
        scene.add(actor.root);
        const camera = new THREE.PerspectiveCamera(30, 1.35, 0.01, 100);
        const d = (Math.max(size.y, size.x / 1.35) / 0.52) * 1.15;
        camera.position.set(
          d * 0.25,
          d * 0.3,
          d * (info.category === "gloves" ? -1 : 1),
        );
        camera.lookAt(0, 0, 0);
        const target = new THREE.WebGLRenderTarget(270, 200);
        target.texture.colorSpace = THREE.SRGBColorSpace;
        const oldTarget = this.renderer.getRenderTarget(),
          oldColor = this.renderer.getClearColor(new THREE.Color()),
          oldAlpha = this.renderer.getClearAlpha();
        const viewport = this.renderer.getViewport(new THREE.Vector4()),
          oldTone = this.renderer.toneMapping;
        this.renderer.setRenderTarget(target);
        this.renderer.setViewport(0, 0, 270, 200);
        this.renderer.setClearColor(0, 0);
        this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
        this.renderer.clear();
        this.renderer.render(scene, camera);
        const pixels = new Uint8Array(270 * 200 * 4);
        this.renderer.readRenderTargetPixels(target, 0, 0, 270, 200, pixels);
        this.renderer.setRenderTarget(oldTarget);
        this.renderer.setViewport(viewport);
        this.renderer.setClearColor(oldColor, oldAlpha);
        this.renderer.toneMapping = oldTone;
        const canvas = document.createElement("canvas");
        canvas.width = 270;
        canvas.height = 200;
        const ctx = canvas.getContext("2d")!,
          image = ctx.createImageData(270, 200);
        for (let y = 0; y < 200; y++)
          image.data.set(
            pixels.subarray((199 - y) * 270 * 4, (200 - y) * 270 * 4),
            y * 270 * 4,
          );
        ctx.putImageData(image, 0, 0);
        // Fit the visible pixels too: skinned source assets can retain invisible arm extents.
        let x0 = 270,
          y0 = 200,
          x1 = 0,
          y1 = 0;
        for (let y = 0; y < 200; y++)
          for (let x = 0; x < 270; x++)
            if (image.data[(x + y * 270) * 4 + 3] > 20) {
              x0 = Math.min(x0, x);
              y0 = Math.min(y0, y);
              x1 = Math.max(x1, x);
              y1 = Math.max(y1, y);
            }
        const tile = document.createElement("canvas");
        tile.width = 270;
        tile.height = 200;
        if (x1 > x0 && y1 > y0) {
          const w = x1 - x0 + 1,
            h = y1 - y0 + 1,
            f = Math.min(242 / w, 174 / h);
          tile
            .getContext("2d")!
            .drawImage(
              canvas,
              x0,
              y0,
              w,
              h,
              (270 - w * f) / 2,
              (200 - h * f) / 2,
              w * f,
              h * f,
            );
        }
        const url = tile.toDataURL("image/webp", 0.9);
        this.thumbnails.set(id, url);
        document
          .querySelectorAll<HTMLImageElement>("[data-thumbnail-id]")
          .forEach((img) => {
            if (img.dataset.thumbnailId === id) img.src = url;
          });
        target.dispose();
        actor.dispose();
        scene.clear();
      }
    } finally {
      this.thumbnailBusy = false;
    }
  }
  drawWeapon() {
    this.playKnife("draw", true);
  }
  inspect() {
    this.playKnife("inspect");
  }
  slash() {
    this.playKnife("slash");
  }
  resetPreview() {
    this.previewRotation = 0;
    this.previewZoom = 1;
  }
  private async previewActor(info: import("./assets.js").AssetInfo) {
    const m = await manifest;
    let actor = await animated(
      info.url,
      info.category === "ct" || info.category === "t" ? "agent" : "item",
    );
    if (info.category === "gloves" && info.rig && m.assets.arms) {
      actor.dispose();
      actor = await animated(m.assets.arms.url, "arms");
      const bones = info.rig.names.map(
        (name) => actor!.root.getObjectByName(name) as THREE.Bone,
      );
      if (bones.every(Boolean)) {
        const skeleton = new THREE.Skeleton(
          bones,
          info.rig.inverses.map((v) => new THREE.Matrix4().fromArray(v)),
        );
        actor.root.traverse((o) => {
          if (
            o instanceof THREE.Mesh &&
            (Array.isArray(o.material) ? o.material : [o.material]).some(
              (mat) => mat.name.includes("glove"),
            )
          )
            o.visible = false;
        });
        const gloves = await loadAsset(info.url);
        gloves.scene.traverse((o) => {
          if (
            o instanceof THREE.Mesh &&
            o.name.includes("viewmodel") &&
            !(Array.isArray(o.material) ? o.material : [o.material]).every(
              (mat) => mat.name.includes("bare_arm"),
            )
          ) {
            const mesh = new THREE.SkinnedMesh(o.geometry, o.material);
            mesh.name = "preview_glove";
            mesh.bind(skeleton, new THREE.Matrix4());
            mesh.frustumCulled = false;
            actor!.root.add(mesh);
          }
        });
      }
      const clip = actor.clips.find(
        (c) =>
          c.name.includes("/_default_knife/") &&
          c.name.split("/").pop()!.startsWith("idle"),
      );
      if (clip) actor.play(clip.name);
    } else if (info.category === "knife" && m.assets.arms) {
      const knife = actor;
      actor = await animated(m.assets.arms.url, "arms");
      actor.root.traverse((o) => {
        if (o instanceof THREE.Mesh) o.visible = false;
      });
      const grip = actor.root.getObjectByName("wpn");
      if (grip) {
        knife.root.quaternion.set(0.5, 0.5, 0.5, 0.5);
        grip.add(knife.root);
      }
      const family = info.animationFamily?.startsWith("knife_default")
        ? "_default_knife"
        : info.animationFamily;
      const idle = actor.clips.find(
        (c) =>
          c.name.includes("/" + family + "/") &&
          c.name.split("/").pop()!.startsWith("idle"),
      );
      if (idle) actor.play(idle.name);
      actor.root.userData.previewIdle = idle?.name;
      actor.root.userData.previewFamily = family;
      actor.root.userData.previewKnife = knife.root;
    } else {
      actor.play("idle_knife");
      if (info.category === "ct" || info.category === "t")
        await Promise.all([
          this.attachKnife(actor, this.loadout.knife),
          this.attachGloves(actor, this.loadout.gloves),
        ]);
    }
    actor.mixer.update(0.05);
    syncPreviewKnife(actor);
    if (info.category === "knife") {
      actor.root.updateMatrixWorld(true);
      const knife = actor.root.userData.previewKnife as THREE.Group | undefined;
      if (knife) {
        const orientation = new THREE.Quaternion()
          .setFromEuler(new THREE.Euler(0, Math.PI / 2, -Math.PI / 5, "ZYX"))
          .multiply(knife.getWorldQuaternion(new THREE.Quaternion()).invert());
        // Present the Karambit's playside; rotate the whole settled pose,
        // preserving its display tilt and animation-relative orientation.
        if (info.animationFamily === "knife_karambit")
          orientation.premultiply(
            new THREE.Quaternion().setFromAxisAngle(
              new THREE.Vector3(0, 1, 0),
              Math.PI,
            ),
          );
        actor.root.quaternion.copy(orientation);
        actor.root.userData.displayQuaternion = orientation;
      }
    }
    return actor;
  }
  async showPreview(id: string) {
    const loading = () =>
      document
        .querySelector(`[data-preview-id="${id}"] .model-loading`)
        ?.remove();
    if (this.previewKey === id && this.previewAsset) {
      loading();
      return;
    }
    this.previewKey = id;
    this.previewAsset = undefined;
    const m = await manifest,
      info = m.assets[id];
    if (!info) return;
    let actor = this.previews.get(id);
    if (!actor) {
      actor = await this.previewActor(info);
      this.previews.set(id, actor);
      // Bound animated preview instances; grid tiles never retain animation mixers.
      if (this.previews.size > 5) {
        const oldest = this.previews.keys().next().value!;
        this.previews.get(oldest)?.dispose();
        this.previews.delete(oldest);
      }
    }
    if (this.previewKey !== id) return;
    this.previewPivot.clear();
    this.previewAsset = actor;
    this.previewClass = info.category;
    actor.root.position.set(0, 0, 0);
    actor.root.rotation.set(0, 0, 0);
    if (actor.root.userData.displayQuaternion)
      actor.root.quaternion.copy(actor.root.userData.displayQuaternion);
    const box = visibleBounds(actor.root, info.category === "gloves"),
      center = box.getCenter(new THREE.Vector3());
    box.getSize(this.previewBounds);
    actor.root.position.sub(center);
    this.previewPivot.add(actor.root);
    const agent = info.category === "ct" || info.category === "t";
    this.previewShadow.visible = agent;
    this.previewShadow.position.y = -this.previewBounds.y / 2 + 0.005;
    this.previewShadow.scale.set(
      this.previewBounds.y * 0.9,
      this.previewBounds.y * 0.7,
      1,
    );
    this.resetPreview();
    loading();
  }
  private async attachKnife(actor: AnimatedAsset, id: string) {
    const info = (await manifest).assets[id];
    if (!info) return;
    const item = await animated(info.url, "item"),
      grip = actor.root.getObjectByName("wpn");
    if (!grip) return;
    item.root.quaternion.set(0.5, 0.5, 0.5, 0.5);
    grip.add(item.root);
  }
  private async attachGloves(actor: AnimatedAsset, id: string) {
    if (id === "gloves-default") return;
    const info = (await manifest).assets[id];
    if (!info) throw Error("Missing glove instance " + id);
    const asset = await loadAsset(info.url);
    const meshes: THREE.SkinnedMesh[] = [];
    if (!info.rig) throw Error("Missing world glove rig " + id);
    const bones = info.rig.names.map(
      (n) => actor.root.getObjectByName(n) as THREE.Bone,
    );
    if (!bones.every(Boolean))
      throw Error("Unresolved world glove bones " + id);
    const skeleton = new THREE.Skeleton(
      bones,
      info.rig.inverses.map((v) => new THREE.Matrix4().fromArray(v)),
    );
    asset.scene.traverse((o) => {
      if (
        o instanceof THREE.Mesh &&
        o.name.includes("worldmodel") &&
        !(Array.isArray(o.material) ? o.material : [o.material]).every((m) =>
          m.name.includes("bare_arm"),
        )
      ) {
        const mesh = new THREE.SkinnedMesh(o.geometry, o.material);
        mesh.bind(skeleton, new THREE.Matrix4());
        mesh.frustumCulled = false;
        meshes.push(mesh);
      }
    });
    if (!meshes.length) throw Error("Missing world glove mesh " + id);
    actor.root.traverse((o) => {
      if (
        o instanceof THREE.Mesh &&
        !o.name.includes("firstperson") &&
        (Array.isArray(o.material) ? o.material : [o.material]).every((m) =>
          /glove/.test(m.name),
        )
      )
        o.visible = false;
    });
    for (const mesh of meshes) {
      mesh.visible = true;
      mesh.name = "equipped_world_glove";
      actor.root.add(mesh);
    }
  }
  async roster(players: any[], myId: string, content: Cosmetic[]) {
    for (const p of players) await registerInstances(p.inventory ?? []);
    for (const [id, g] of this.remotes)
      if (!players.some((p) => p.id === id)) {
        this.scene.remove(g);
        this.clear(g);
        this.remotes.delete(id);
      }
    for (const p of players)
      if (p.id !== myId) {
        let g = this.remotes.get(p.id);
        const appearance = JSON.stringify([p.team, p.equipped]);
        if (g && g.userData.appearance !== appearance) {
          this.scene.remove(g);
          this.clear(g);
          this.remotes.delete(p.id);
          g = undefined;
        }
        if (!g) {
          const c = [...content, ...(p.inventory ?? [])].find(
            (c) => c.id === p.equipped.agent,
          );
          g = new THREE.Group();
          g.userData.appearance = appearance;
          const name = label(p.name, "#e7eadc", 15);
          name.position.z = 92;
          g.add(name);
          g.visible = false;
          this.remotes.set(p.id, g);
          this.scene.add(g);
          if (c?.asset) {
            const target = g;
            void animated(c.asset)
              .then((actor) => {
                target.clear();
                actor.root.quaternion.set(0.5, 0.5, 0.5, 0.5);
                actor.root.scale.setScalar(1 / 0.0254);
                target.add(actor.root, name);
                target.userData.actor = actor;
                void Promise.all([
                  this.attachKnife(actor, p.equipped.knife),
                  this.attachGloves(actor, p.equipped.gloves),
                ]).catch(console.warn);
              })
              .catch(console.warn);
          }
        }
      }
  }
  setRemote(id: string, state: any, dt: number) {
    const g = this.remotes.get(id);
    if (!g) return;
    if (!g.userData.hasState) g.position.set(...(state.p as Vec3));
    g.userData.hasState = true;
    g.visible = true;
    g.position.lerp(new THREE.Vector3(...state.p), 1 - Math.exp(-dt * 18));
    g.rotation.z = state.yaw;
    if (g.userData.actor) {
      g.scale.z = 1;
      (g.userData.actor as AnimatedAsset).movement(state, dt);
      return;
    }
  }
  setGhost(state: State | null) {
    if (!state) {
      if (this.ghost) this.ghost.visible = false;
      return;
    }
    if (!this.ghost) {
      this.ghost = new THREE.Group();
      const target = this.ghost;
      void manifest.then(async (m) => {
        const actor = await animated(m.assets["ct-default"].url);
        actor.root.quaternion.set(0.5, 0.5, 0.5, 0.5);
        actor.root.scale.setScalar(1 / 0.0254);
        actor.root.traverse((o) => {
          if (o instanceof THREE.Mesh) {
            const materials = (
              Array.isArray(o.material) ? o.material : [o.material]
            ).map((m) => {
              const copy = m.clone();
              copy.transparent = true;
              copy.opacity = 0.28;
              return copy;
            });
            o.material = Array.isArray(o.material) ? materials : materials[0];
          }
        });
        target.add(actor.root);
        target.userData.actor = actor;
      });
      this.scene.add(this.ghost);
    }
    this.ghost.visible = settings.ghosts;
    this.ghost.position.set(...state.p);
    this.ghost.rotation.z = state.yaw;
    this.ghost.userData.actor?.movement(state, 1 / 100);
  }
  draw(
    state: State | null,
    menu: boolean,
    dt: number,
    tick: number,
    debug: boolean,
    firstPerson = true,
  ) {
    this.renderer.info.reset();
    this.debug.visible = debug;
    if (state) {
      const eye = state.duck ? 30 : 64;
      this.camera.position.set(state.p[0], state.p[1], state.p[2] + eye);
      const direction = new THREE.Vector3(
        Math.cos(state.yaw) * Math.cos(state.pitch),
        Math.sin(state.yaw) * Math.cos(state.pitch),
        Math.sin(state.pitch),
      );
      if (!firstPerson)
        this.camera.position
          .addScaledVector(direction, -130)
          .add(new THREE.Vector3(0, 0, 45));
      this.camera.lookAt(this.camera.position.clone().add(direction));
    } else if (this.map) {
      const t = performance.now() / 1000;
      this.camera.position.set(650 + Math.sin(t * 0.06) * 100, -680, 510);
      this.camera.lookAt(1550, 0, 0);
    }
    for (const g of this.remotes.values())
      g.visible =
        settings.otherPlayers &&
        !!g.userData.hasState &&
        !!g.userData.actor &&
        (!state ||
          !firstPerson ||
          g.position.distanceTo(new THREE.Vector3(...state.p)) > 48);
    for (let i = 0; i < this.platformMeshes.length; i++) {
      const p = this.map!.platforms![i];
      this.platformMeshes[i].position.set(
        ...(p.axis.map(
          (v) =>
            v * Math.sin((tick / 100 / p.period) * Math.PI * 2) * p.amplitude,
        ) as Vec3),
      );
    }
    this.renderer.setViewport(0, 0, innerWidth, innerHeight);
    this.renderer.autoClear = true;
    if (menu) {
      this.renderer.setClearColor("#343c43");
      this.renderer.clear();
    } else this.renderer.render(this.scene, this.camera);
    const preview = menu
      ? document.querySelector<HTMLElement>("[data-model-preview]")
      : null;
    if (preview && this.previewAsset) {
      const r = preview.getBoundingClientRect();
      const actor = this.previewAsset;
      if (!actor.oneShot && actor.root.userData.previewIdle)
        actor.play(actor.root.userData.previewIdle);
      actor.mixer.update(dt);
      syncPreviewKnife(actor);
      this.previewPivot.rotation.y = this.previewRotation;
      const agent = this.previewClass === "ct" || this.previewClass === "t";
      const gloves = this.previewClass === "gloves";
      this.previewCamera.aspect = r.width / Math.max(1, r.height);
      this.previewCamera.fov = agent ? 32 : 35;
      const halfFov = THREE.MathUtils.degToRad(this.previewCamera.fov / 2);
      const fit = Math.max(
        this.previewBounds.y,
        this.previewBounds.x / this.previewCamera.aspect,
      );
      const distance =
        (fit / (2 * Math.tan(halfFov)) + this.previewBounds.z * 0.35) *
        this.previewZoom *
        (agent ? 1.08 : 1.15);
      this.previewCamera.position.set(
        distance * (agent ? 0.28 : gloves ? 0.02 : 0.25),
        distance * (agent ? 0.06 : gloves ? 0.25 : 0.25),
        distance * (gloves ? -1 : 1),
      );
      this.previewCamera.lookAt(0, 0, 0);
      this.previewCamera.updateProjectionMatrix();
      this.renderer.setScissorTest(true);
      this.renderer.setScissor(
        r.left,
        innerHeight - r.bottom,
        r.width,
        r.height,
      );
      this.renderer.setViewport(
        r.left,
        innerHeight - r.bottom,
        r.width,
        r.height,
      );
      this.renderer.clearDepth();
      this.renderer.autoClear = false;
      this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
      this.renderer.toneMappingExposure = 1.05;
      this.renderer.render(this.previewScene, this.previewCamera);
      this.renderer.toneMapping = THREE.NoToneMapping;
      this.renderer.setScissorTest(false);
      this.renderer.setViewport(0, 0, innerWidth, innerHeight);
    }
    if (state && !menu && firstPerson) {
      if (this.arms) {
        if (!this.arms.oneShot) this.playKnife("idle");
        this.arms.mixer.update(dt);
        if (this.knifeModel && this.grip) {
          this.arms.root.updateMatrixWorld(true);
          this.viewmodel.worldToLocal(
            this.grip.getWorldPosition(this.gripPosition),
          );
          this.knifeModel.position.copy(this.gripPosition);
          this.viewmodel.getWorldQuaternion(this.parentRotation).invert();
          this.knifeModel.quaternion
            .copy(this.parentRotation)
            .multiply(this.grip.getWorldQuaternion(this.gripRotation))
            .multiply(this.weaponBasis);
          for (const { source, target } of this.knifeLinks) {
            target.position.copy(source.position);
            target.quaternion.copy(source.quaternion);
            target.scale.copy(source.scale);
          }
        }
      }
      const bob =
        settings.viewmodelBob === "off"
          ? 0
          : settings.viewmodelBob === "low"
            ? 0.08
            : 0.2;
      this.viewmodel.position.y =
        Math.sin(performance.now() * 0.01) *
        Math.min(Math.hypot(...state.v) / 1500, 1) *
        bob;
      this.renderer.autoClear = false;
      this.renderer.clearDepth();
      this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
      this.renderer.toneMappingExposure = 1.05;
      this.renderer.render(this.viewScene, this.viewCamera);
      this.renderer.toneMapping = THREE.NoToneMapping;
    }
  }
}
